import { Button, InlineLink, Tooltip } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { Dashboard, ObjectType } from '@fullstory/proto/fs/services/dx-objects';
import { generateRoutePath, PLAN_UPGRADE_ROUTE } from '@fullstory/routes';
import React, { ComponentPropsWithoutRef, ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { noPermissionMessage } from '../../utils';
import { AddToDashboardModal } from '../AddToDashboardModal';

export type AddToDashboardObjectTypes = Extract<
  ObjectType,
  ObjectType.METRIC | ObjectType.FUNNEL | ObjectType.RETENTION | ObjectType.HEATMAP
>;

interface AddToDashboardButtonProps
  extends Pick<
    ComponentPropsWithoutRef<typeof AddToDashboardModal>,
    'onSave' | 'returnToDashboard' | 'objectVisibility'
  > {
  objectType: AddToDashboardObjectTypes;
  /**
   * Whether the object is saved or not.
   *
   * Before an object is saved, the button appears disabled with a tooltip.
   */
  objectSaved: boolean;
  /**
   * Whether dashboards are enabled on the org.
   *
   * If not, the button appears disabled with a tooltip.
   *
   * @default false
   */
  dashboardsEnabled: boolean;
  /**
   * Whether the user has permission to add to dashboard or is in view only.
   *
   * If not, the button appears disabled with a tooltip and lock icon.
   *
   * @default false
   */
  isViewOnly?: boolean;
  orgId: string;
}

export const AddToDashboardButton: React.FC<AddToDashboardButtonProps> = ({
  objectType,
  objectSaved,
  dashboardsEnabled,
  isViewOnly,
  orgId,
  returnToDashboard,
  objectVisibility,
  onSave,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const disabled = !objectSaved || !dashboardsEnabled || isViewOnly;
  let tooltipContent: React.ReactNode = '';
  if (isViewOnly) {
    tooltipContent = intl.formatMessage(noPermissionMessage);
  } else if (!dashboardsEnabled) {
    // dashboards not enabled takes precedence over a not saved message
    tooltipContent = intl.formatMessage(
      {
        defaultMessage:
          "Dashboards aren't included in your current plan. <comparePlansLink>Compare plans</comparePlansLink> and learn how to add them.",
        id: 'S7pWES',
      },
      {
        comparePlansLink: (text: ReactNode) => (
          <InlineLink to={generateRoutePath(PLAN_UPGRADE_ROUTE, { orgId })}>{text}</InlineLink>
        ),
      },
    );
  } else if (!objectSaved) {
    tooltipContent = intl.formatMessage(
      {
        defaultMessage:
          '{objectType, select, 1 {Metric} 2 {Funnel} 3 {Retention} 7 {Heatmap} other {unknown type}} must be saved if you want to add to a Dashboard',
        id: 'EFFfm0',
      },
      {
        objectType,
      },
    );
  }

  return (
    <>
      <Tooltip disabled={!disabled} tooltipContent={tooltipContent}>
        <Button
          testId="add-to-dashboard"
          disabled={disabled}
          onClick={() => setIsModalOpen(true)}
          iconLeft={isViewOnly ? 'lock' : undefined}
          iconRight={dashboardsEnabled && !isViewOnly ? 'dashboard' : undefined}
          variant="outline"
        >
          <FormattedMessage defaultMessage="Add to Dashboard" id="2sG+o0" />
        </Button>
      </Tooltip>
      {isModalOpen && (
        <AddToDashboardModal
          showTitle={false}
          onCloseSignal={() => setIsModalOpen(false)}
          returnToDashboard={returnToDashboard}
          disableDashboards={(dashboard: Dashboard) =>
            objectVisibility === Visibility.PRIVATE && dashboard.visibility !== Visibility.PRIVATE
          }
          objectVisibility={objectVisibility}
          onSave={metadata => {
            onSave(metadata);
            setIsModalOpen(false);
          }}
        />
      )}
    </>
  );
};
